"use client";
import {
  LoaderComponent,
  PaperComponent,
  PincodeValidation,
  ProgressComponent,
} from "@/components";
import { useRouter } from "@/i18n/routing";
import {
  Box,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import styles from "./resume-journey.module.css";

import {
  CurrencyConverter,
  GET_PRODUCT_APPLICATION,
  IMAGES,
  PRODUCT_HANDLER,
  STAGE_BASED_ROUTING,
  WEB_COLORS,
} from "@/lib/constants";
import { useFooter } from "@/lib/context/FooterContext";
import useProgressHook from "@/lib/hooks/progressHook";
import { useGetApplicationQuery, useGetCustomerQuery } from "@/lib/redux";
import Image from "next/image";
import StepperComponent from "./component/stepperComponent";

const steps = [
  {
    title: "Personal Details",
    content: "Start by sharing your PAN to verify your identity Loan Offer",
  },
  {
    title: "Loan Offer",
    content: "Pick the loan amount and best offer that suits your needs",
  },
  {
    title: "KYC Verification",
    content:
      "Complete quick KYC using Aadhaar /DigiLocker and your selfie verification",
  },
  {
    title: "Work Details",
    content: "Tell us about your employment to help us assess your eligibility",
  },
  {
    title: "Re-Payment Setup",
    content:
      "Provide your Banking details & complete the KYC verification with your selfie.",
  },
  {
    title: "E-sign",
    content:
      "Digitally sign and accept you Loan offer to complete your application",
  },
];

const ResumeJourneyScreen = () => {
  const router = useRouter();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isBelowSm = useMediaQuery(theme.breakpoints.down("sm"));
  const { setActions } = useFooter();

  const { data: customerData } = useGetCustomerQuery();
  const { data: applicationData } = useGetApplicationQuery();
  const productApplication = GET_PRODUCT_APPLICATION(
    PRODUCT_HANDLER["personal-loan"],
    applicationData?.data,
  );

  const { activeStage } = useProgressHook(productApplication?.stage);
  const [resumeSteps, setResumeSteps] = useState([]);
  const [openPincodeModal, setOpenPincodeModal] = useState(false);
  const [loanAmount, setLoanAmount] = useState(0);

  useEffect(() => {
    if (activeStage > -1) {
      let newSteps = steps?.filter((val, index) => index >= activeStage);
      setResumeSteps(newSteps);
    }
  }, [activeStage]);

  useEffect(() => {
    setActions({
      showReject: false,
      showConfirm: false,
    });
  }, [setActions]);

  useEffect(() => {
    if (applicationData?.data?.applications.length) {
      let { requestedLoanAmount, selectOfferAmount, bureauFinalLoanAmount } =
        applicationData?.data?.applications[0];
      setLoanAmount(
        Number(selectOfferAmount) ||
          Number(bureauFinalLoanAmount) ||
          Number(requestedLoanAmount),
      );
    }
  }, [applicationData?.data]);

  const handleRouting = () => {
    router.replace(STAGE_BASED_ROUTING(applicationData?.data?.stage));
  };

  const handleResumeJourney = () => {
    if (applicationData?.data?.serviceablePincode) {
      setOpenPincodeModal(true);
    } else if (productApplication?.stage && customerData?.data?.id) {
      router.replace(STAGE_BASED_ROUTING(productApplication?.stage));
    }
  };

  return (
    <>
      <LoaderComponent open={false} />
      <PaperComponent
        elevation={0}
        paperStyle={{
          backgroundColor: "#F2F7F799",
          px: { xs: 0, md: 2 },
          py: { xs: 0, md: 2 },
        }}
      >
        {!loanAmount ? (
          <Stack
            p={0}
            sx={{
              border: "1px solid #CCD6D4",
              borderRadius: "12px",
              backgroundColor: "#E9FFE6",
            }}
            className={styles["radial-lines"]}
          >
            <Stack
              p={2}
              color={WEB_COLORS.darkTeal}
              flexDirection={"column"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <Box width={{ xs: "300px", md: "620px" }}>
                <Typography
                  fontWeight={500}
                  fontSize={{ xs: "16px", md: "20px" }}
                  textAlign={"center"}
                >
                  Welcome back{" "}
                  {customerData?.data?.firstName
                    ? customerData?.data?.firstName
                    : "Guest"}
                  !
                </Typography>
                <Typography
                  fontWeight={400}
                  fontSize={{ xs: "16px", md: "20px" }}
                  mt={1}
                  textAlign={"center"}
                >
                  You&apos;re almost there! You&apos;re just{" "}
                </Typography>
                <Typography
                  fontWeight={400}
                  fontSize={{ xs: "16px", md: "20px" }}
                  textAlign={"center"}
                >
                  <Typography
                    fontWeight={700}
                    component={"span"}
                    fontSize={{ xs: "16px", md: "20px" }}
                    display={"inline-block"}
                  >
                    {6 - activeStage} steps away
                  </Typography>{" "}
                  from getting your loan sanctioned.
                </Typography>
              </Box>
            </Stack>
          </Stack>
        ) : (
          <Grid
            container
            p={0}
            sx={{
              border: "1px solid #CCD6D4",
              borderRadius: "12px",
              backgroundColor: "#E9FFE6",
              flexDirection: { xs: "column", md: "row" },
            }}
            className={isMobile ? styles["radial-lines"] : ""}
          >
            <Grid
              item
              xs={12}
              md={6}
              p={2}
              color={WEB_COLORS.darkTeal}
              textAlign={{ xs: "center", md: "left" }}
              flexDirection={"row"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <Box width={{ md: "375px" }}>
                <Typography
                  fontWeight={500}
                  fontSize={{ xs: "16px", md: "20px" }}
                >
                  Welcome back{" "}
                  {customerData?.data?.firstName
                    ? customerData?.data?.firstName
                    : "Guest"}
                  !
                </Typography>
                <Typography
                  fontWeight={400}
                  fontSize={{ xs: "16px", md: "20px" }}
                  mt={2}
                >
                  You’re almost there! You’re just{" "}
                  <Typography
                    component={"span"}
                    fontWeight={700}
                    fontSize={{ xs: "16px", md: "20px" }}
                    display={"inline-block"}
                  >
                    {6 - activeStage} steps away
                  </Typography>{" "}
                  from getting your loan sanctioned.
                </Typography>
              </Box>
            </Grid>
            <Grid
              container
              className={isMobile ? "" : styles["resume-journey"]}
              // xs={12}
              md={6}
              textAlign={"center"}
              justifyContent={"center"}
              alignItems={"center"}
              height={{ xs: "150px", md: "173px" }}
              sx={{
                position: { xs: "relative", md: "" },
                overflowX: { xs: "clip", md: "none" },
              }}
            >
              {isMobile && (
                <Box>
                  <Image
                    alt="coins"
                    className={styles["left"]}
                    src={IMAGES.coinsLeft}
                  />
                </Box>
              )}
              <Box sx={{ zIndex: 1 }}>
                <Typography
                  fontWeight={400}
                  fontSize={"16px"}
                  color={WEB_COLORS.darkTeal}
                  sx={{ mt: { xs: -4, md: 0 } }}
                >
                  Loan Amount
                </Typography>
                <Typography
                  fontWeight={700}
                  sx={{ mt: { xs: 4, md: 0 } }}
                  fontSize={{ xs: "32px", md: "44px" }}
                  color={WEB_COLORS.darkTeal}
                >
                  {CurrencyConverter(loanAmount)}
                </Typography>
              </Box>
              {isMobile && (
                <Box>
                  <Image
                    alt="coins"
                    className={styles["right"]}
                    src={IMAGES.coinsRight}
                  />
                </Box>
              )}
            </Grid>
          </Grid>
        )}
        <Stack my={2}>
          <ProgressComponent
            product={"personal-loan"}
            isStepperRequired={false}
          />
        </Stack>
        <Stack>
          <StepperComponent
            steps={resumeSteps}
            handleResumeJourney={handleResumeJourney}
          />
        </Stack>
      </PaperComponent>
      <PincodeValidation
        open={openPincodeModal}
        setOpen={setOpenPincodeModal}
        route={handleRouting}
      />
    </>
  );
};

export default ResumeJourneyScreen;
